// =============================================================================
// MeuDriver — Política de Privacidade (rota pública /privacidade)
// -----------------------------------------------------------------------------
// Estrutura LGPD-aware (Lei 13.709/2018). Texto base — submeter à revisão
// jurídica antes de publicar em produção.
// =============================================================================

import { useSEO } from '@/hooks/useSEO'

const VIGENCIA = '21 de maio de 2026'

export default function PrivacyPage() {
  useSEO({
    title: 'Política de Privacidade',
    description:
      'Como o MeuDriver coleta, usa, compartilha e protege seus dados pessoais — em conformidade com a LGPD.',
  })

  return (
    <div className="bg-white min-h-screen">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-gray prose-sm sm:prose-base">
        <header className="not-prose mb-8 space-y-2 border-b border-gray-100 pb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Política de Privacidade
          </h1>
          <p className="text-sm text-gray-500">
            Vigência: {VIGENCIA}. Esta Política é regida pela Lei Geral de
            Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD).
          </p>
        </header>

        <h2>1. Controlador dos dados</h2>
        <p>
          O MeuDriver atua como <strong>controlador</strong> dos dados pessoais
          de alunos e instrutores cadastrados na Plataforma. Para contato com
          o nosso Encarregado de Proteção de Dados (DPO), use o canal de
          suporte em /ajuda.
        </p>

        <h2>2. Dados coletados</h2>
        <p>Coletamos os seguintes dados, conforme o tipo de conta:</p>
        <ul>
          <li>
            <strong>Cadastrais:</strong> nome completo, e-mail, telefone, CPF
            (quando aplicável), data de nascimento (quando aplicável).
          </li>
          <li>
            <strong>De instrutor:</strong> CNH, foto do veículo, comprovante de
            endereço, bio profissional, dados bancários para repasse.
          </li>
          <li>
            <strong>Operacionais:</strong> agendamentos, mensagens trocadas
            entre aluno e instrutor, avaliações, histórico de pagamentos.
          </li>
          <li>
            <strong>Técnicos:</strong> endereço IP, identificadores de
            dispositivo, logs de acesso, cookies (vide seção 8).
          </li>
          <li>
            <strong>Localização:</strong> apenas quando o usuário fornece
            voluntariamente um endereço para agendamento.
          </li>
        </ul>

        <h2>3. Bases legais e finalidades</h2>
        <p>
          Tratamos seus dados com base em uma das seguintes hipóteses legais
          (arts. 7º e 11 da LGPD):
        </p>
        <ul>
          <li>
            <strong>Execução de contrato:</strong> para fornecer a Plataforma,
            processar pagamentos, mediar agendamentos.
          </li>
          <li>
            <strong>Consentimento:</strong> para envio de comunicações de
            marketing e analytics opcional (Posthog). O consentimento pode ser
            revogado a qualquer momento.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal:</strong> retenção de dados
            fiscais e financeiros pelos prazos exigidos por lei.
          </li>
          <li>
            <strong>Legítimo interesse:</strong> prevenção a fraudes,
            segurança da Plataforma, melhoria do serviço.
          </li>
        </ul>

        <h2>4. Compartilhamento</h2>
        <p>
          Compartilhamos dados apenas com:
        </p>
        <ul>
          <li>
            <strong>Provedores de infraestrutura:</strong> Supabase (banco de
            dados e autenticação) e CDN parceira.
          </li>
          <li>
            <strong>Gateway de pagamento:</strong> Mercado Pago, para
            processamento de transações.
          </li>
          <li>
            <strong>E-mail transacional:</strong> Resend, para envio de
            confirmações e notificações.
          </li>
          <li>
            <strong>Observabilidade:</strong> Sentry (erros) e Posthog
            (analytics opcional).
          </li>
          <li>
            <strong>Outro usuário da Plataforma:</strong> ao agendar uma aula,
            o aluno compartilha nome, telefone e local com o instrutor (e
            vice-versa) na extensão necessária para a aula.
          </li>
          <li>
            <strong>Autoridades:</strong> quando obrigatório por lei ou ordem
            judicial.
          </li>
        </ul>

        <h2>5. Transferência internacional</h2>
        <p>
          Alguns parceiros (ex.: Sentry, Posthog) podem armazenar dados em
          servidores fora do Brasil. Adotamos cláusulas contratuais e práticas
          alinhadas à LGPD para garantir nível adequado de proteção.
        </p>

        <h2>6. Retenção</h2>
        <ul>
          <li>
            Dados de conta: enquanto a conta estiver ativa, e por até 5 anos
            após o encerramento, para fins fiscais e de disputa.
          </li>
          <li>
            Dados financeiros: pelo prazo exigido pela legislação fiscal
            (geralmente 5 anos).
          </li>
          <li>
            Mensagens de chat: 12 meses após o último uso, salvo se vinculadas
            a disputa em aberto.
          </li>
        </ul>

        <h2>7. Direitos do titular (LGPD)</h2>
        <p>Você tem direito a:</p>
        <ul>
          <li>Confirmação e acesso aos seus dados;</li>
          <li>Correção de dados incompletos ou desatualizados;</li>
          <li>
            Anonimização, bloqueio ou eliminação de dados desnecessários;
          </li>
          <li>Portabilidade dos dados a outro fornecedor;</li>
          <li>
            Eliminação dos dados tratados com base em consentimento;
          </li>
          <li>Informação sobre compartilhamentos;</li>
          <li>Revogação do consentimento.</li>
        </ul>
        <p>
          Para exercer esses direitos, entre em contato pelo canal de suporte
          em <a href="/ajuda">/ajuda</a>. Atendemos solicitações em até 15
          dias.
        </p>

        <h2>8. Cookies</h2>
        <p>
          Usamos cookies estritamente necessários (sessão, preferências) e,
          mediante seu consentimento, cookies analíticos (Posthog). Você pode
          desabilitá-los nas configurações do seu navegador a qualquer
          momento.
        </p>

        <h2>9. Segurança</h2>
        <p>
          Aplicamos controles técnicos e organizacionais alinhados às boas
          práticas do setor: criptografia em trânsito (HTTPS), políticas de
          acesso por papel (RLS no banco), MFA opcional, monitoramento de
          incidentes, e auditoria periódica.
        </p>

        <h2>10. Crianças e adolescentes</h2>
        <p>
          A Plataforma é destinada a maiores de 18 anos. Menores entre 16 e 18
          anos podem usar com autorização do responsável legal. Não tratamos
          dados de menores de 16 anos.
        </p>

        <h2>11. Mudanças nesta Política</h2>
        <p>
          Atualizações relevantes serão comunicadas por e-mail e/ou
          notificação na Plataforma com antecedência mínima de 7 dias antes da
          vigência.
        </p>

        <h2>12. Contato</h2>
        <p>
          Dúvidas sobre privacidade ou para exercer seus direitos:{' '}
          <a href="/ajuda">canal de suporte</a>.
        </p>

        <p className="text-xs text-gray-400 mt-8">
          Versão {VIGENCIA}. Pendente de revisão jurídica final antes da
          abertura ao público em produção.
        </p>
      </article>
    </div>
  )
}
